import { Pie } from "react-chartjs-2"
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from "chart.js"

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale)

interface PieChartData {
  label: string
  value: number
  color: string
}

interface PieChartProps {
  data: PieChartData[]
  totalPayment?: number
  size?: number
}

function formatFixedWidthPercentage(value: number): string {
  // Convert to percentage with two decimals
  const percentValue = (value * 100).toFixed(2); // e.g., "4.50"

  // Pad to fixed width (up to 6 characters before adding %)
  // "04.50" -> "04.50", "4.50" -> "04.50", "100.00" stays as is
  const padded = percentValue.padStart(5, '0');

  // Add % sign and make it total width 7 (optional alignment)
  return padded + '%';
}

export function PieChart({ data, totalPayment, size = 350 }: PieChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0)

  if (total === 0) {
    return (
      <div className="flex items-center justify-center"
      // styles={{ width: size, height: size }}
      >
        <p className="text-gray-400 text-sm">No data</p>
      </div>
    )
  }

  // Convert data to Chart.js format
  const chartData = {
    labels: data.map((item) => item.label),
    datasets: [
      {
        data: data.map((item) => item.value),
        backgroundColor: data.map((item) => item.color),
        borderColor: "#ffffff",
        borderWidth: 2,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    animation: {
      animateRotate: true,
      animateScale: false,
      duration: 1000,
      easing: 'easeOutCubic' as const,
    },
    transitions: {
      show: {
        animation: {
          duration: 800,
          easing: 'easeOutCubic' as const,
        },
      },
      hide: {
        animation: {
          duration: 400,
          easing: 'easeInCubic' as const,
        },
      },
    },
    plugins: {
      legend: {
        display: false, // We'll show custom legend below
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || ""
            const value = context.parsed || 0
            const percentage = (value / total)
            return `${label}: $${value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} (${formatFixedWidthPercentage(percentage)})`
          },
        },
      },
    },
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <div
      // style={{ width: size, height: size }}
      >
        <Pie data={chartData} options={options} />
      </div>
      <table className="w-full border-collapse border-0">
        <tbody className="border-0">
          {data.map((item, index) => {
            const percentage = item.value / total
            return (
              <tr key={index} className="border-0">
                <td className="border-0 py-1 pr-3">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: item.color }}
                  />
                </td>
                <td className="border-0 py-1 text-sm text-gray-600">
                  {item.label}
                </td>
                <td className="border-0 py-1 text-sm text-right font-medium font-mono tabular-nums">
                  ${item.value.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                </td>
                <td className="border-0 py-1 text-sm text-right font-medium font-mono tabular-nums">
                  ({formatFixedWidthPercentage(percentage)})
                </td>
              </tr>
            )
          })}
          <tr className="border-0 border-t border-gray-300">
            <td className="border-0 py-2 pt-3" colSpan={2}>
              <span className="text-sm text-gray-700 font-semibold">Total:</span>
            </td>
            <td className="border-0 py-2 pt-3 text-sm text-right font-bold text-gray-900 font-mono tabular-nums">
              ${total.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}